import React, { useState, useEffect } from 'react';
import { X, Loader2, AlertCircle, Download } from 'lucide-react';
import reportConfigurationService from '../../../../services/reportConfigurationService';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface PDFPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportId: number;
}

export const PDFPreviewModal: React.FC<PDFPreviewModalProps> = ({
  isOpen,
  onClose,
  reportId
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const companyNo = user?.companyNo;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string>('');

  useEffect(() => {
    if (isOpen && reportId) {
      loadPreview();
    }

    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [isOpen, reportId]);

  const loadPreview = async () => {
    if (!companyNo) return;

    setLoading(true);
    setError(null);

    try {
      const blob = await reportConfigurationService.getPreview(companyNo, reportId);
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (err: any) {
      console.error('Failed to load preview:', err);
      setError(err.message || t('manager.modules.adminHost.pdfPreview.loadError', {}, 'Kunne ikke laste forhåndsvisning'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!pdfUrl) return;

    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `Report_${reportId}_Preview.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-5xl h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {t('manager.modules.adminHost.pdfPreview.title', { '0': reportId }, `Forhåndsvisning - Rapport #${reportId}`)}
          </h3>
          <div className="flex items-center gap-2">
            {pdfUrl && !loading && (
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                title={t('manager.modules.adminHost.pdfPreview.downloadPdf', {}, 'Last ned PDF')}
              >
                <Download size={16} />
                {t('manager.modules.adminHost.pdfPreview.download', {}, 'Last ned')}
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              title={t('common.buttons.close', {}, 'Lukk')}
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden relative bg-gray-100">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <Loader2 className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" />
                <p className="text-gray-600">{t('manager.modules.adminHost.pdfPreview.generating', {}, 'Genererer forhåndsvisning...')}</p>
              </div>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-white rounded-lg shadow-lg p-6 max-w-md">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-red-600 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">{t('manager.modules.adminHost.pdfPreview.loadError', {}, 'Kunne ikke laste forhåndsvisning')}</h4>
                    <p className="text-gray-600 text-sm mb-4">{error}</p>
                    <div className="flex gap-2">
                      <button
                        onClick={loadPreview}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                      >
                        {t('manager.modules.adminHost.common.tryAgain', {}, 'Prøv igjen')}
                      </button>
                      <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                      >
                        {t('common.buttons.close', {}, 'Lukk')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {pdfUrl && !loading && !error && (
            <iframe
              src={pdfUrl}
              className="w-full h-full border-0"
              title="PDF Preview"
            />
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <p className="text-xs text-gray-500 text-center">
            {t('manager.modules.adminHost.pdfPreview.demoDataNote', {}, 'Dette er en forhåndsvisning med demo-data. Faktisk utskrift vil bruke reelle data fra systemet.')}
          </p>
        </div>
      </div>
    </div>
  );
};
